<template>
  <div>
    <v-form ref="form" @input="onChange">
    <v-row>
      <v-col cols="12">
        <h3 class="mb-4">Osnovni podaci firme</h3>
      </v-col>
    </v-row>

    <!-- Osnovni podaci -->
    <v-row>
      <v-col cols="12" md="6">
        <div class="d-flex align-center">
          <v-text-field
            autofocus
            dense
            outlined
            label="OIB"
            v-model="company.oib"
            v-mask="'###########'"
            :rules="[rules.req(), rules.oib()]"
            :loading="oibLookupLoading"
            hide-details="auto"
          />
          <field-help-dialog
            title="OIB - Osobni identifikacijski broj"
            description="Jedinstveni 11-znamenkasti broj koji služi za identifikaciju poreznih obveznika u Hrvatskoj. Dodjeljuje ga Porezna uprava."
            :required="true"
            format="11 znamenaka (npr. 12345678901)"
            example="12345678901"
            notes="OIB firme možete pronaći na rješenju o upisu u registar ili na dokumentima Porezne uprave"
          />
        </div>
      </v-col>
      <v-col cols="12" md="6">
        <div class="d-flex align-center">
          <v-text-field
            dense
            outlined
            label="Naziv firme"
            v-model="company.name"
            :rules="[rules.req()]"
            hide-details="auto"
          />
          <field-help-dialog
            title="Naziv firme"
            description="Puni pravni naziv vaše tvrtke ili obrta kako je upisan u sudskom ili obrtnom registru. Ovaj naziv će se prikazivati na računima i svim službenim dokumentima."
            :required="true"
            example="Primjer d.o.o.: TEHNOLOGIJA I PROMET d.o.o. / Primjer obrt: Obrt za trgovinu IVAN HORVAT"
            notes="Naziv mora odgovarati onome iz registra i uključivati pravnu formu (d.o.o., obrt, itd.)"
          />
        </div>
      </v-col>
    </v-row>

    <v-row>
      <v-col cols="12" md="6">
        <div class="d-flex align-center">
          <v-text-field
            dense
            outlined
            label="Adresa"
            v-model="company.address"
            :rules="[rules.req()]"
            hide-details="auto"
          />
          <field-help-dialog
            title="Adresa sjedišta"
            description="Puna adresa sjedišta tvrtke sa nazivom ulice i kućnim brojem. Ova adresa mora odgovarati adresi upisanoj u službenom registru."
            :required="true"
            example="Ilica 242, Savska cesta 32"
            notes="Unesite točnu adresu kako je navedena u registru - koristit će se na računima i službenim dokumentima"
          />
        </div>
      </v-col>
      <v-col cols="12" md="6">
        <div class="d-flex align-center">
          <v-text-field
            dense
            outlined
            label="Grad"
            v-model="company.city"
            :rules="[rules.req()]"
            hide-details="auto"
          />
          <field-help-dialog
            title="Grad sjedišta"
            description="Naziv grada ili naselja gdje se nalazi sjedište tvrtke. Mora odgovarati adresi iz registra."
            :required="true"
            example="Zagreb, Split, Rijeka"
            notes="Unesite naziv grada bez dodatnih oznaka ili poštanskog broja"
          />
        </div>
      </v-col>
    </v-row>

    <v-row>
      <v-col cols="12" md="6">
        <div class="d-flex align-center">
          <v-text-field
            dense
            outlined
            label="Poštanski broj"
            v-model="company.zip_code"
            :rules="[rules.req()]"
            hide-details="auto"
          />
          <field-help-dialog
            title="Poštanski broj"
            description="5-znamenkasti poštanski broj koji odgovara adresi sjedišta tvrtke."
            :required="true"
            format="5 znamenaka (npr. 10000)"
            example="10000 (Zagreb), 21000 (Split), 51000 (Rijeka)"
            notes="Provjerite točan poštanski broj na stranicama Hrvatske pošte"
          />
        </div>
      </v-col>
      <v-col cols="12" md="6">
        <div class="d-flex align-center">
          <div class="country-field-wrapper" style="flex: 1;">
            <div class="country-field-label">Država</div>
            <div class="country-field-value">Hrvatska</div>
          </div>
          <field-help-dialog
            title="Država"
            description="Država u kojoj je registrirana vaša tvrtka. Fiksno je postavljena Hrvatska."
            :required="true"
            example="Hrvatska"
            notes="Ovo polje je fiksno postavljeno na Hrvatsku i ne može se mijenjati."
          />
        </div>
      </v-col>
    </v-row>

    <v-row>
      <v-col cols="12" md="6">
        <div class="d-flex align-center">
          <v-select
            dense
            outlined
            label="Tip firme"
            v-model="company.type"
            :items="companyTypes"
            item-text="name"
            item-value="value"
            :rules="[rules.req()]"
            hide-details="auto"
          />
          <field-help-dialog
            title="Tip firme"
            description="Pravna forma organizacije vašeg poslovanja. Određuje način oporezivanja i obveze izvještavanja."
            :required="true"
            notes="D.O.O. i J.D.O.O. plaćaju porez na dobit, dok obrtnička djelatnost i paušalni obrt plaćaju porez na dohodak. Za D.O.O. i J.D.O.O. morat ćete unijeti dodatne poslovne podatke."
          />
        </div>
      </v-col>
      <v-col cols="12" md="6">
        <div class="d-flex align-center">
          <v-checkbox
            v-model="company.is_taxed"
            label="U sustavu PDV-a"
            hide-details="auto"
          />
          <field-help-dialog
            title="U sustavu PDV-a"
            description="Označite ovu opciju ako je vaša tvrtka registrirana kao obveznik PDV-a (poreza na dodanu vrijednost)."
            :required="false"
            notes="Obveznici PDV-a moraju obračunavati 25% PDV na sve usluge i proizvode (osim onih sa sniženom stopom)."
          />
        </div>
      </v-col>
    </v-row>

    <!-- Banka -->
    <v-row class="mt-6">
      <v-col cols="12">
        <h3 class="mb-4">Podaci o banci</h3>
      </v-col>
    </v-row>

    <v-row>
      <v-col cols="12" md="6">
        <div class="d-flex align-center">
          <v-text-field
            dense
            outlined
            label="IBAN"
            v-model="iban"
            :rules="[rules.req()]"
            :color="ibanColor"
            @input="onIbanInput"
            hide-details="auto"
          />
          <field-help-dialog
            title="IBAN - Međunarodni broj bankovnog računa"
            description="Jedinstveni identifikator vašeg poslovnog bankovnog računa. IBAN se koristi za primanje uplata od kupaca i plaćanja dobavljačima."
            :required="true"
            format="HR + 19 znamenaka (ukupno 21 znak)"
            example="HR1234567890123456789"
            notes="Unesite IBAN primarnog poslovnog računa. Polje će se obojati zeleno kada unesete ispravan IBAN, što će automatski popuniti podatke o banci."
          />
        </div>
      </v-col>
      <v-col cols="12" md="6">
        <div class="d-flex align-center">
          <v-combobox
            dense
            outlined
            label="SWIFT kod"
            v-model="swift"
            :items="banks"
            item-text="swift"
            item-value="swift"
            :disabled="bankFieldsDisabled"
            @change="findBankData"
            hide-details="auto"
          />
          <field-help-dialog
            title="SWIFT kod banke"
            description="Međunarodni identifikacijski kod banke (BIC/SWIFT kod). Automatski se popunjava nakon unosa ispravnog IBAN-a."
            :required="true"
            format="8 ili 11 znakova (npr. ZABAHR2X)"
            example="ZABAHR2X (Zagrebačka banka), PBZGHR2X (Privredna banka)"
            notes="Polje se automatski aktivira i popunjava nakon što unesete ispravan IBAN. Možete ručno promijeniti ako je potrebno."
          />
        </div>
      </v-col>
    </v-row>

    <v-row>
      <v-col cols="12" md="6">
        <div class="d-flex align-center">
          <v-text-field
            dense
            outlined
            label="Naziv banke"
            v-model="bank.name"
            :disabled="bankFieldsDisabled"
            hide-details="auto"
          />
          <field-help-dialog
            title="Naziv banke"
            description="Puni naziv financijske institucije kod koje imate otvoren poslovni račun."
            :required="true"
            example="Zagrebačka banka d.d., Privredna banka Zagreb d.d."
            notes="Ovo polje se automatski popunjava nakon unosa ispravnog IBAN-a. Ne morate ga ručno mijenjati osim ako sustav nije prepoznao banku."
          />
        </div>
      </v-col>
      <v-col cols="12" md="6">
        <div class="d-flex align-center">
          <v-text-field
            dense
            outlined
            label="Adresa banke"
            v-model="bank.street"
            :disabled="bankFieldsDisabled"
            hide-details="auto"
          />
          <field-help-dialog
            title="Adresa banke"
            description="Adresa sjedišta banke ili podružnice kod koje je otvoren račun."
            :required="false"
            example="Trg bana Josipa Jelačića 10"
            notes="Automatski se popunjava iz baze banaka. Informativno polje koje se prikazuje na nekim dokumentima."
          />
        </div>
      </v-col>
    </v-row>

    <v-row>
      <v-col cols="12" md="4">
        <div class="d-flex align-center">
          <v-text-field
            dense
            outlined
            label="Grad banke"
            v-model="bank.city"
            :disabled="bankFieldsDisabled"
            hide-details="auto"
          />
          <field-help-dialog
            title="Grad banke"
            description="Grad u kojem se nalazi sjedište ili podružnica banke."
            :required="false"
            example="Zagreb"
            notes="Automatski se popunjava na temelju IBAN-a."
          />
        </div>
      </v-col>
      <v-col cols="12" md="4">
        <div class="d-flex align-center">
          <v-text-field
            dense
            outlined
            label="Država banke"
            v-model="bank.country"
            :disabled="bankFieldsDisabled"
            hide-details="auto"
          />
          <field-help-dialog
            title="Država banke"
            description="Država u kojoj je registrirana banka."
            :required="false"
            example="Hrvatska"
            notes="Automatski se popunjava. Za hrvatske banke uvijek će biti 'Hrvatska'."
          />
        </div>
      </v-col>
      <v-col cols="12" md="4">
        <div class="d-flex align-center">
          <v-text-field
            dense
            outlined
            label="Poštanski broj banke"
            v-model="bank.zip_code"
            :disabled="bankFieldsDisabled"
            hide-details="auto"
          />
          <field-help-dialog
            title="Poštanski broj banke"
            description="Poštanski broj lokacije banke."
            :required="false"
            example="10000"
            notes="Automatski se popunjava iz baze podataka banaka."
          />
        </div>
      </v-col>
    </v-row>

    <!-- Poslovni podaci (samo za D.O.O. i J.D.O.O.) -->
    <template v-if="company.type === 'doo' || company.type === 'jdoo'">
      <v-row class="mt-6">
        <v-col cols="12">
          <h3 class="mb-4">Poslovni podaci</h3>
        </v-col>
      </v-row>

      <v-row>
        <v-col cols="12" md="6">
          <div class="d-flex align-center">
            <v-text-field
              dense
              outlined
              label="Matični broj"
              v-model="company.legal_info.court_register_id"
              :rules="[rules.req()]"
              hide-details="auto"
            />
            <field-help-dialog
              title="Matični broj (MBS)"
              description="Matični broj subjekta (MBS) je jedinstveni broj koji dodjeljuje trgovački sud prilikom upisa društva u sudski registar."
              :required="true"
              format="Obično 8-12 znamenaka"
              example="080563571"
              notes="Matični broj možete pronaći na rješenju o upisu u sudski registar ili na izvodu iz sudskog registra. Za J.D.O.O. ovo je obavezno."
            />
          </div>
        </v-col>
        <v-col cols="12" md="6">
          <div class="d-flex align-center">
            <v-autocomplete
              dense
              outlined
              label="Sud"
              v-model="company.legal_info.court"
              :items="courts"
              :rules="[rules.req()]"
              hide-details="auto"
            />
            <field-help-dialog
              title="Nadležni trgovački sud"
              description="Trgovački sud koji je nadležan za upis i vođenje podataka o vašem društvu."
              :required="true"
              example="Trgovački sud u Zagrebu, Trgovački sud u Splitu"
              notes="Nadležnost suda određena je prema sjedištu društva. Odaberite sud iz padajućeg popisa."
            />
          </div>
        </v-col>
      </v-row>

      <v-row>
        <v-col cols="12" md="6">
          <div class="d-flex align-center">
            <v-text-field
              dense
              outlined
              label="Temeljni kapital"
              v-model="company.legal_info.base_capital"
              type="number"
              :rules="[rules.req()]"
              hint="Unesite iznos u valuti"
              persistent-hint
            />
            <field-help-dialog
              title="Temeljni kapital"
              description="Iznos temeljnog kapitala društva koji je upisan u sudski registar. To je minimalni kapital potreban za osnivanje društva."
              :required="true"
              format="Unesite broj (npr. 20000 za 20.000 EUR)"
              example="20000 (za D.O.O. minimum), 10 (za J.D.O.O. minimum)"
              notes="Za D.O.O. minimalni temeljni kapital je 20.000 EUR (ili protuvrijednost u kunama). Za J.D.O.O. minimalni je 10 kuna (ili 1,33 EUR). Unesite točan iznos upisan u sudskom registru."
            />
          </div>
        </v-col>
        <v-col cols="12" md="6">
          <div class="d-flex align-center">
            <v-select
              dense
              outlined
              label="Valuta"
              v-model="company.legal_info.base_capital_currency"
              :items="currencies"
              :rules="[rules.req()]"
              hide-details="auto"
            />
            <field-help-dialog
              title="Valuta temeljnog kapitala"
              description="Valuta u kojoj je izražen temeljni kapital društva u sudskom registru."
              :required="true"
              example="EUR (za nova društva), HRK (za društva osnована prije uvođenja eura)"
              notes="Od 2023. godine nova društva upisuju temeljni kapital u eurima (EUR). Starija društva mogu još imati iznos upisan u kunama (HRK)."
            />
          </div>
        </v-col>
      </v-row>

      <!-- Članovi uprave -->
      <v-row class="mt-4">
        <v-col cols="12">
          <div class="d-flex align-center mb-2">
            <h4>Članovi uprave</h4>
            <v-btn
              small
              color="primary"
              icon
              @click="addMember"
              class="ml-2"
            >
              <v-icon>mdi-plus</v-icon>
            </v-btn>
            <field-help-dialog
              title="Članovi uprave"
              description="Popis osoba ovlaštenih za zastupanje društva - direktori, članovi uprave ili prokurist. Ovi podaci moraju odgovarati upisima u sudskom registru."
              :required="true"
              example="Direktor: Ivan Horvat / Član uprave: Ana Marić"
              notes="Morate dodati najmanje jednu osobu. Označite je kao 'Direktor' ako ima funkciju direktora društva. Za D.O.O. i J.D.O.O. ovo su osobe s ovlasti potpisa."
            />
          </div>
          <v-alert
            v-if="company.legal_info.members.length === 0"
            dense
            outlined
            type="warning"
          >
            Morate dodati najmanje jednog člana uprave
          </v-alert>
          <v-list dense v-else>
            <v-list-item
              v-for="(member, index) in company.legal_info.members"
              :key="index"
            >
              <v-list-item-content>
                <v-list-item-title>
                  {{ member.first_name }} {{ member.last_name }}
                  <v-chip x-small v-if="member.ceo" class="ml-2">Direktor</v-chip>
                </v-list-item-title>
              </v-list-item-content>
              <v-list-item-action>
                <v-btn icon small @click="removeMember(index)">
                  <v-icon small>mdi-delete</v-icon>
                </v-btn>
              </v-list-item-action>
            </v-list-item>
          </v-list>
        </v-col>
      </v-row>
    </template>
  </v-form>

  <!-- Dialog za dodavanje člana uprave -->
  <v-dialog v-model="memberDialog" max-width="500px" persistent>
    <v-card>
      <v-card-title>Dodaj člana uprave</v-card-title>
      <v-card-text>
        <v-form ref="memberForm">
          <v-text-field
            v-model="newMember.first_name"
            label="Ime"
            :rules="[rules.req()]"
            outlined
            dense
          />
          <v-text-field
            v-model="newMember.last_name"
            label="Prezime"
            :rules="[rules.req()]"
            outlined
            dense
          />
          <v-checkbox
            v-model="newMember.ceo"
            label="Direktor"
          />
        </v-form>
      </v-card-text>
      <v-card-actions>
        <v-spacer />
        <v-btn text @click="cancelMember">Odustani</v-btn>
        <v-btn color="primary" @click="saveMember">Spremi</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
  </div>
</template>

<script>
import rules from '@/plugins/rules'
import { df } from '@/plugins/firebase'
import FieldHelpDialog from '../shared/FieldHelpDialog.vue'

export default {
  name: 'StepCompany',
  components: {
    FieldHelpDialog
  },
  props: {
    value: {
      type: Object,
      required: true
    }
  },
  data () {
    return {
      componentKey: 0,
      company: {
        name: '',
        oib: '',
        address: '',
        city: '',
        country: 'Hrvatska',
        zip_code: '',
        type: 'pausalni_obrt',
        tax_type: 'income',
        is_taxed: false,
        bank_accounts: [],
        legal_info: {
          court_register_id: '',
          court: '',
          base_capital: 0,
          base_capital_currency: 'EUR',
          members: [],
          bank: {}
        }
      },
      iban: '',
      swift: '',
      bank: {
        name: '',
        street: '',
        city: '',
        country: '',
        zip_code: '',
        leading_bank_num: ''
      },
      bankFieldsDisabled: true,
      ibanColor: '',
      banks: [],
      courts: [],
      companyTypes: [
        { name: 'd.o.o.', value: 'doo' },
        { name: 'j.d.o.o.', value: 'jdoo' },
        { name: 'Obrt', value: 'obrt' },
        { name: 'Paušalni obrt', value: 'pausalni_obrt' }
      ],
      currencies: ['EUR', 'HRK'],
      rules,
      memberDialog: false,
      newMember: {
        first_name: '',
        last_name: '',
        ceo: true
      },
      oibLookupLoading: false
    }
  },
  watch: {
    company: {
      handler (newVal) {
        this.$emit('input', newVal)
      },
      deep: true
    },
    'company.oib' (val) {
      if (val && val.length === 11) {
        this.lookupByOib(val)
      }
    }
  },
  methods: {
    async loadBanks () {
      try {
        const bankCollection = await df.doc('project_constants/banks').get()
        if (bankCollection?.data()?.banks) {
          this.banks = Object.values(bankCollection.data().banks)
        }
      } catch (error) {
        console.error('Error loading banks:', error)
        this.banks = []
      }
    },
    async loadCourts () {
      try {
        const courtsCollection = await df.doc('project_constants/courts').get()
        if (courtsCollection?.data()?.courts) {
          this.courts = courtsCollection.data().courts
        }
      } catch (error) {
        console.error('Error loading courts:', error)
        this.courts = []
      }
    },
    validate () {
      const formValid = this.$refs.form.validate()

      // Provjera IBAN-a i bank podataka
      if (!this.iban || !this.swift || !this.bank.name) {
        return false
      }

      // Provjera za D.O.O. i J.D.O.O.
      if (this.company.type === 'doo' || this.company.type === 'jdoo') {
        const legalInfoValid =
          this.company.legal_info.court_register_id &&
          this.company.legal_info.court &&
          this.company.legal_info.base_capital &&
          this.company.legal_info.base_capital_currency &&
          this.company.legal_info.members.length > 0

        return formValid && legalInfoValid
      }

      return formValid
    },
    onChange () {
      this.$emit('change')
    },
    onIbanInput () {
      const ibantools = require('ibantools')

      if (ibantools.isValidIBAN(this.iban)) {
        this.ibanColor = 'success'
        this.bankFieldsDisabled = false
        this.extractBankInfo()
      } else {
        this.ibanColor = 'error'
        this.bankFieldsDisabled = true
      }
    },
    extractBankInfo () {
      const leadingBankNum = this.iban.substring(4, 11)
      this.bank.leading_bank_num = leadingBankNum

      const foundBank = this.banks.find(b => b.leading_bank_num === leadingBankNum)
      if (foundBank) {
        this.swift = foundBank.swift
        this.findBankData()
      }
    },
    findBankData () {
      const selectedBank = this.banks.find(b => b.swift === this.swift)
      if (selectedBank) {
        this.bank.name = selectedBank.name
        this.bank.street = selectedBank.street
        this.bank.city = selectedBank.city
        this.bank.country = selectedBank.country
        this.bank.zip_code = selectedBank.zip_code
      }
    },
    addMember () {
      this.memberDialog = true
    },
    saveMember () {
      if (this.$refs.memberForm && this.$refs.memberForm.validate()) {
        this.company.legal_info.members.push({
          first_name: this.newMember.first_name,
          last_name: this.newMember.last_name,
          ceo: this.newMember.ceo
        })
        this.cancelMember()
      }
    },
    cancelMember () {
      this.memberDialog = false
      this.newMember = {
        first_name: '',
        last_name: '',
        ceo: true
      }
      if (this.$refs.memberForm) {
        this.$refs.memberForm.resetValidation()
      }
    },
    removeMember (index) {
      this.company.legal_info.members.splice(index, 1)
    },
    async lookupByOib (oib) {
      this.oibLookupLoading = true
      try {
        const response = await fetch('https://europe-west1-ai-fisk-stage.cloudfunctions.net/businessentites', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ data: { filter: 'oib', oib } })
        })
        const json = await response.json()
        const entity = json.result?.business_entity || json.data?.business_entity || json.business_entity
        if (entity) {
          if (entity.name) this.company.name = entity.name
          if (entity.headquatersAddress) this.company.address = entity.headquatersAddress
          if (entity.headquatersCity) this.company.city = entity.headquatersCity
          if (entity.headquatersZip) this.company.zip_code = entity.headquatersZip
        }
      } catch (error) {
        console.error('OIB lookup failed:', error)
      } finally {
        this.oibLookupLoading = false
      }
    },
    getData () {
      // Priprema bank_accounts
      const bankAccount = {
        iban: this.iban,
        swift: this.swift,
        bank: this.bank,
        default: true
      }

      this.company.bank_accounts = [bankAccount]

      // Za tip koji nije D.O.O. ili J.D.O.O., legal_info se postavlja na null
      if (this.company.type !== 'doo' && this.company.type !== 'jdoo') {
        this.company.legal_info = null
      } else {
        // Za D.O.O. i J.D.O.O., tax_type mora biti 'profit'
        this.company.tax_type = 'profit'

        // Konvertuj base_capital u broj (u "centima" - pomnoženo sa 100)
        if (this.company.legal_info.base_capital) {
          this.company.legal_info.base_capital = parseInt(parseFloat(this.company.legal_info.base_capital) * 100)
        }
      }

      return this.company
    }
  },
  async mounted () {
    // Inkrementuj componentKey za re-render
    this.componentKey++

    // Učitaj banke i sudove
    await this.loadBanks()
    await this.loadCourts()

    // Inicijaliziraj s vrijednostima iz props ako postoje
    if (this.value && Object.keys(this.value).length > 0) {
      // Merguj value sa default vrijednostima, ali zadrži default vrednosti za boolean polja
      const mergedCompany = {
        ...this.company,
        ...this.value
      }

      // Zadrži default false za is_taxed ako je null ili undefined
      if (this.value.is_taxed === null || this.value.is_taxed === undefined) {
        mergedCompany.is_taxed = false
      }

      this.company = mergedCompany

      if (this.company.bank_accounts && this.company.bank_accounts.length > 0) {
        const bankAccount = this.company.bank_accounts[0]
        this.iban = bankAccount.iban
        this.swift = bankAccount.swift
        this.bank = bankAccount.bank
      }
    }

    // Osiguraj da country uvijek ima vrijednost 'Hrvatska'
    this.company.country = this.company.country || 'Hrvatska'

    // Emit promjenu nakon što je sve postavljeno
    this.$nextTick(() => {
      this.$emit('input', this.company)
    })
  }
}
</script>

<style scoped>
h3, h4 {
  color: #1577da;
}

.country-field-wrapper {
  position: relative;
  border: 1px solid rgba(0, 0, 0, 0.38);
  border-radius: 4px;
  padding: 18px 12px 8px 12px;
  background: #f5f5f5;
  min-height: 40px;
}

.country-field-label {
  position: absolute;
  top: 4px;
  left: 12px;
  font-size: 12px;
  color: rgba(0, 0, 0, 0.6);
  pointer-events: none;
}

.country-field-value {
  font-size: 16px;
  color: rgba(0, 0, 0, 0.87);
  line-height: 20px;
}
</style>
